<script setup lang="ts">
import { computed } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import IconPalm from '@/components/IconPalm.vue';
import AppHeader from '@/components/AppHeader.vue';
import { useRouter } from 'vue-router';

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

const showShell = computed(() => route.name !== 'login' && auth.isAuthenticated);
const showBootSkeleton = computed(() => !auth.restored);

async function onLogout() {
  await auth.logout();
  router.replace('/login');
}
</script>

<template>
  <div class="app-root">
    <div class="app-bg" aria-hidden="true" />
    <div class="app-palm" aria-hidden="true">
      <IconPalm :size="720" />
    </div>

    <AppHeader :show-shell="showShell" :user="auth.user" shell-width="var(--shell-width)" @logout="onLogout" />

    <main class="app-main" :class="{ 'is-auth': !showShell, 'with-shell': showShell }">
      <div v-if="showBootSkeleton" class="boot-skeleton" aria-hidden="true">
        <span class="skeleton skeleton-line skeleton-title" />
        <span class="skeleton skeleton-line" />
        <span class="skeleton skeleton-line" />
      </div>
      <RouterView v-else />
    </main>
  </div>
</template>

<style lang="scss" scoped>
.app-root {
  --shell-width: min(1120px, calc(100% - 2rem));
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: clip;
}

.app-bg {
  position: fixed;
  inset: 0;
  background: var(--page-gradient);
  z-index: 0;
  pointer-events: none;
}

.app-palm {
  position: fixed;
  bottom: -60px;
  left: 50%;
  transform: translateX(-50%);
  opacity: var(--palm-opacity);
  z-index: 1;
  pointer-events: none;
}

.app-main {
  flex: 1;
  width: var(--shell-width);
  margin: 0 auto;
  padding: 2rem 1.5rem;
  position: relative;
  z-index: 2;

  &.with-shell {
    padding-top: 7.1rem;
  }

  &.is-auth {
    max-width: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 0px);
  }
}

.boot-skeleton {
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.skeleton {
  border-radius: 10px;
  background: linear-gradient(90deg,
      rgb(from var(--color-light) r g b / 0.35) 0%,
      rgb(from var(--color-light) r g b / 0.6) 50%,
      rgb(from var(--color-light) r g b / 0.35) 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s ease-in-out infinite;
}

.skeleton-line {
  height: 18px;
}

.skeleton-title {
  height: 30px;
  width: 42%;
}

@keyframes skeleton-shimmer {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}
</style>
